/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { findIndex, last } from 'lodash';

import { Group, Item, Point, Raster, Shape } from 'paper';
import {
  GraphicalTakeOffImageTransform,
  GraphicalTakeOffImageTransformPipeline,
  GraphicalTakeOffImageTransformType,
} from '../../resources/models';
import { toPaperPoint } from './canvas.utils';

export const DEFAULT_DPI = 72;

const applyRasterTransformation = (item: Item, transformation: GraphicalTakeOffImageTransform) => {
  switch (transformation.type) {
    case GraphicalTakeOffImageTransformType.Rotate:
      item.rotation = transformation.angle;
      return item;
    case GraphicalTakeOffImageTransformType.Crop:
      item.firstChild.replaceWith(Shape.Rectangle(
        toPaperPoint(transformation.lower),
        toPaperPoint(transformation.upper)));
      return item;
    default:
      return item;
  }
};

export const setItemPosition = (item: Item, pipeline: GraphicalTakeOffImageTransformPipeline) => {
  item.position = new Point(pipeline.originalSize.width / 2, pipeline.originalSize.height / 2);
  return item;
};

export const newRaster = (image: HTMLImageElement, pipeline: GraphicalTakeOffImageTransformPipeline) =>
  setItemPosition(
    new Raster({
      image,
      data: {
        id: 'raster',
      },
    }),
    pipeline);

export const applyRasterPipeline = (item: Item,
                                    image: HTMLImageElement,
                                    pipeline: GraphicalTakeOffImageTransformPipeline,
                                    previousPipeline?: GraphicalTakeOffImageTransformPipeline): Item => {
  if (previousPipeline && pipeline.transformations.length < previousPipeline.transformations.length) {
    // number of transformations was reduced => rebuild whole transformation pipeline
    item.remove();
    return applyRasterPipeline(newRaster(image, pipeline), image, pipeline);
  }
  const differentTransformationIndex = previousPipeline ?
    findIndex(
      previousPipeline.transformations,
      (previousTransformation, i) => previousTransformation !== pipeline.transformations[i])
    : -1;
  if (differentTransformationIndex >= 0) {
    const lastTransformation = last(pipeline.transformations)!;
    const lastPreviousTransformation = last(previousPipeline!.transformations)!;
    if (differentTransformationIndex === pipeline.transformations.length - 1
      && lastPreviousTransformation.type === lastTransformation.type) {
      // if only the last one transformation was changed and it is rotation transformation => change rotation angle
      return applyRasterTransformation(item, lastTransformation);
      // raster.rotation = lastTransformation.angle;
      // return raster;
    } else {
      // otherwise rebuild whole transformation pipeline
      item.remove();
      return applyRasterPipeline(newRaster(image, pipeline), image, pipeline);
    }
  }

  // some new transformations were applied, old transformations were not changed => just apply new transformations
  const newTransformations = pipeline.transformations
    .slice(previousPipeline ? previousPipeline.transformations.length : 0);

  return newTransformations.reduce(
    (previousRaster, transformation) => {
      switch (transformation.type) {
        case GraphicalTakeOffImageTransformType.Crop: {
          const group = new Group({
            applyMatrix: false,
            children: [
              Shape.Rectangle(toPaperPoint(transformation.lower), toPaperPoint(transformation.upper)),
              previousRaster,
            ],
          });
          group.clipped = true;
          setItemPosition(group, pipeline);
          return group;
        }
        case GraphicalTakeOffImageTransformType.Rotate: {
          const group = new Group({
            applyMatrix: false,
            rotation: transformation.angle,
            children: [previousRaster],
          });
          setItemPosition(group, pipeline);
          return group;
        }
        default:
          return previousRaster;
      }
    },
    item);
};

const POSITION_OUTSIDE_VIEWPORT = new Point(10000, 10000);

export const createVirtualRaster = (image: HTMLImageElement,
                                    pipeline: GraphicalTakeOffImageTransformPipeline) => {
  const raster = applyRasterPipeline(newRaster(image, pipeline), image, pipeline);
  raster.position = POSITION_OUTSIDE_VIEWPORT;
  return raster;
};
